import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { createPortal } from "react-dom";
import CoTraderFilters from "./CoTraderFilter";
import {
  fetchSymbols,
  useInsightsWs,
  useLiveTickerQuote,
} from "./Utilities/CoTraderUtils";
import { useLiveSentiment } from "./Utilities/useLiveSentiment";
import CoTraderInsightsPanel from "./CoTraderInsightsPanel";
import CoTraderEmgptDiv from "./CoTraderEmgpt";
import CoTraderDemoLockedChatPanel from "./CoTraderDemoLockedChatPanel";
import CoTraderBarChart from "./CoTraderChart/CoTraderBarChart";
import CoTraderExecutionDecision from "./CoTraderExecutionDecision";
import Switch from "react-switch";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { FaBars } from "react-icons/fa";
import { Button, Dropdown } from "react-bootstrap";
import CoTraderCustomTickerTape from "../TradingView/CoTraderTickerTape";
import TradingViewChart from "../TradingView/TradingViewChart";
import { useStablePrependScroll } from "./useStablePrependScroll";
import { getUserId } from "../../utilities/authHelper";
import {
  COTRADER_LANGUAGE_LIST,
  getStoredCoTraderLanguage,
} from "./Utilities/languages";
import WorkspaceSettingsSection from "../Common/WorkspaceSettingsSection";
import WorkspacePresetPicker from "../Common/WorkspacePresetPicker";
// import CoTraderCustomTickerTape from "../TradingView/CoTraderTickerTape";

const FX_SPOT_CHART_SYMBOL_MAP = {
  EURUSD: "EURUSD=X",
  USDJPY: "JPY=X",
  GBPUSD: "GBPUSD=X",
  USDCHF: "CHF=X",
  USDCAD: "CAD=X",
  AUDUSD: "AUDUSD=X",
  NZDUSD: "NZDUSD=X",
  USDNOK: "NOK=X",
  USDSEK: "SEK=X",
  USDCNY: "CNY=X",
  USDCLP: "CLP=X",
  USDCOP: "COP=X",
  USDMYR: "MYR=X",
  EURHUF: "EURHUF=X",
  USDBRL: "BRL=X",
  USDARS: "ARS=X",
  USDPEN: "PEN=X",
  USDKRW: "KRW=X",
  USDTWD: "TWD=X",
  USDTHB: "THB=X",
  USDMAD: "MAD=X",
  USDHKD: "HKD=X",
  USDPHP: "PHP=X",
  EURPLN: "EURPLN=X",
  USDTRY: "TRY=X",
  USDMXN: "MXN=X",
  USDZAR: "ZAR=X",
  USDIDR: "IDR=X",
  EURRON: "EURRON=X",
  USDINR: "USDINR=X",
};

const getFxSpotTickerStreamSymbol = (symbol) => {
  const normalizedSymbol = String(symbol || "").trim();
  if (!normalizedSymbol) return "";
  return /=X$/i.test(normalizedSymbol) ? normalizedSymbol : `${normalizedSymbol}=X`;
};

const mapFxSymbolsToTickerTape = (symbols, selectedSymbol) => {
  const normalizedSelected = getFxSpotTickerStreamSymbol(selectedSymbol);
  const nextSymbols = [
    normalizedSelected,
    ...symbols.map((symbol) => getFxSpotTickerStreamSymbol(symbol?.value || symbol)),
  ].filter(Boolean);

  return Array.from(new Set(nextSymbols));
};
const FX_WORKSPACE_VERSION = "v4";
// Hosts a DOM node that outlives layout changes. The assistant is portaled
// into that node once, so switching workspace presets moves it between slots
// instead of remounting it (which would wipe the conversation).
const PersistentSlot = ({ node }) => {
  const slotRef = useRef(null);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot || !node) return undefined;
    slot.appendChild(node);
    return () => {
      if (node.parentNode === slot) slot.removeChild(node);
    };
  }, [node]);

  return <div ref={slotRef} className="h-100" />;
};

// Bumped when the default insights/assistant split changes, so saved panel
// sizes from the old split do not override it.
const FX_ROW_LAYOUT_REVISION = "r2";
const FX_WORKSPACE_PRODUCT = "co-trader-fx";
const DEFAULT_FX_WORKSPACE = {
  preset: "desk",
  shellLayout: "stacked",
  chartPosition: "top",
  gptPosition: "right",
};

const FX_WORKSPACE_PRESETS = {
  desk: {
    label: "Desk",
    description: "Chart top, market left, FX GPT right",
    config: DEFAULT_FX_WORKSPACE,
  },
  research: {
    label: "Research",
    description: "Chart top, FX GPT left, compact sentiment above updates",
    config: {
      preset: "research",
      shellLayout: "stacked",
      chartPosition: "top",
      gptPosition: "left",
    },
  },
  split: {
    label: "Split View",
    description: "Chart and updates stack on one side, FX GPT spans the other",
    config: {
      preset: "split",
      shellLayout: "assistantColumn",
      chartPosition: "top",
      gptPosition: "right",
    },
  },
  flow: {
    label: "Flow",
    description: "Chart bottom, updates and execution lead",
    config: {
      preset: "flow",
      shellLayout: "stacked",
      chartPosition: "bottom",
      gptPosition: "right",
    },
  },
  tape: {
    label: "Tape",
    description: "Chart hidden, market flow first",
    config: {
      preset: "tape",
      shellLayout: "stacked",
      chartPosition: "collapsed",
      gptPosition: "right",
    },
  },
};

const FX_SHELL_LAYOUT_OPTIONS = ["stacked", "assistantColumn"];
const FX_CHART_POSITION_OPTIONS = ["top", "bottom", "collapsed"];
const FX_GPT_POSITION_OPTIONS = ["right", "left", "collapsed"];
const FX_UPDATES_TABS = [
  ["updates", "Insights"],
  ["execution", "Algo vs RFQ"],
];

const getFxWorkspaceStorageKey = (userId) =>
  `${FX_WORKSPACE_PRODUCT}:workspace:${FX_WORKSPACE_VERSION}:${userId || "anonymous"}`;

const getFxWorkspacePresetConfig = (presetKey) =>
  FX_WORKSPACE_PRESETS[presetKey]?.config || DEFAULT_FX_WORKSPACE;

const normalizeFxWorkspace = (rawConfig = {}) => {
  const preset =
    rawConfig.preset && FX_WORKSPACE_PRESETS[rawConfig.preset]
      ? rawConfig.preset
      : "custom";
  const presetBase =
    preset === "custom" ? DEFAULT_FX_WORKSPACE : getFxWorkspacePresetConfig(preset);

  const shellLayout = FX_SHELL_LAYOUT_OPTIONS.includes(rawConfig.shellLayout)
    ? rawConfig.shellLayout
    : presetBase.shellLayout;
  const chartPosition = FX_CHART_POSITION_OPTIONS.includes(rawConfig.chartPosition)
    ? rawConfig.chartPosition
    : presetBase.chartPosition;
  const gptPosition = FX_GPT_POSITION_OPTIONS.includes(rawConfig.gptPosition)
    ? rawConfig.gptPosition
    : presetBase.gptPosition;

  return {
    preset,
    shellLayout,
    chartPosition,
    gptPosition,
  };
};

const getInitialFxWorkspace = (userId) => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(getFxWorkspaceStorageKey(userId)) || "null"
    );

    if (parsed) {
      return normalizeFxWorkspace(parsed);
    }
  } catch (error) {
    console.warn("Failed to load FX CoTrader workspace config", error);
  }

  return DEFAULT_FX_WORKSPACE;
};

const CoTrader = ({
  availableSymbolsOverride = null,
  chartIntervalOverride = null,
  chartPeriodOverride = null,
  hideTopChrome = false,
  hideSelectionSummary = false,
  isDemoMode = false,
  instrumentTypeOverride = null,
  selectedSymbolOverride = null,
  toolbarPortalTarget = null,
}) => {
  const pageTitle = "FX CoTrader";
  const currentUserId = useMemo(() => getUserId() || "anonymous", []);
  const workspaceStorageKey = useMemo(
    () => getFxWorkspaceStorageKey(currentUserId),
    [currentUserId]
  );
  const WORKSPACE_THEME = {
    pageBg: "var(--app-page-bg, #eef3f8)",
    border: "var(--app-border, #E5EAF1)",
    panelBg: "var(--app-panel-bg, #FFFFFF)",
    panelAltBg: "var(--app-panel-alt-bg, #F8FAFC)",
    text: "var(--app-text, #0B1220)",
    muted: "var(--app-muted, #5B6472)",
  };
  const [updateSpeed, setUpdateSpeed] = useState(2);
  const [selectedSymbol, setSelectedSymbol] = useState(
    selectedSymbolOverride || localStorage.getItem("default_symbol") || "USDJPY"
  );
  const [tenor, setTenor] = useState([]);
  const [tenorList, setTenorList] = useState([]);
  const showCountDownInsights = true;
  const selectedSymbolRef = useRef(selectedSymbol);
  const [searchFlag, setSearchFlag] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [activeUpdatesTab, setActiveUpdatesTab] = useState("updates");
  const [showMenu, setShowMenu] = useState(false);
  const [settingsCollapseSignal, setSettingsCollapseSignal] = useState(0);
  const [language, setLanguage] = useState(() => getStoredCoTraderLanguage());
  const [workspaceConfig, setWorkspaceConfig] = useState(() =>
    getInitialFxWorkspace(currentUserId)
  );
  const [languageKey, setLanguageKey] = useState(0);
  const [layoutResetKey, setLayoutResetKey] = useState(0);
  const [chartLayoutVersion, setChartLayoutVersion] = useState(0);
  const [fxgptState, setFxgptState] = useState({
    hasConversation: false,
    isLoading: false,
  });
  const [pendingFxQuestion, setPendingFxQuestion] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [gptHostNode] = useState(() => {
    const node = document.createElement("div");
    node.className = "h-100";
    return node;
  });
  const menuRef = useRef(null);
  const [activeTvSymbol, setActiveTvSymbol] = useState(null);
  const [instrumentType, setInstrumentType] = useState(
    instrumentTypeOverride || "FX_SPOT"
  );
  const [fxAvailableSymbols, setFxAvailableSymbols] = useState([]);
  const [scrollSpeed, setScrollSpeed] = useState(30);

  const cotraderMode = useMemo(
    () => (fxgptState.hasConversation || fxgptState.isLoading ? "ask" : "monitor"),
    [fxgptState.hasConversation, fxgptState.isLoading]
  );
  const GptPanelComponent = isDemoMode
    ? CoTraderDemoLockedChatPanel
    : CoTraderEmgptDiv;
  const showChart = workspaceConfig.chartPosition !== "collapsed";
  const showGpt = workspaceConfig.gptPosition !== "collapsed";
  const currentUpdatesTab =
    activeUpdatesTab === "execution" ? "execution" : "updates";

  const layoutState = useMemo(
    () => ({
      verticalId: `cotrader-layout-vertical-${cotraderMode}-${workspaceConfig.shellLayout}-${workspaceConfig.chartPosition}-${FX_WORKSPACE_VERSION}`,
      horizontalId: `cotrader-layout-horizontal-${cotraderMode}-${workspaceConfig.shellLayout}-${workspaceConfig.gptPosition}-${FX_WORKSPACE_VERSION}-${FX_ROW_LAYOUT_REVISION}`,
      shellHorizontalId: `cotrader-layout-shell-${cotraderMode}-${workspaceConfig.shellLayout}-${workspaceConfig.gptPosition}-${FX_WORKSPACE_VERSION}-${FX_ROW_LAYOUT_REVISION}`,
      contentVerticalId: `cotrader-layout-content-${cotraderMode}-${workspaceConfig.shellLayout}-${workspaceConfig.chartPosition}-${FX_WORKSPACE_VERSION}`,
      chartPanelSize: cotraderMode === "ask" ? 26 : 32,
      // Wide range: the chart/footer handle is always available.
      chartPanelMin: 12,
      chartPanelMax: 72,
      bottomPanelSize: cotraderMode === "ask" ? 74 : 68,
      bottomPanelMin: 28,
      // Insights and the assistant split the row evenly by default.
      marketPanelSize: 50,
      marketPanelMin: 30,
      marketPanelMax: 70,
      fxgptPanelSize: 50,
      fxgptPanelMin: 30,
      contentPanelSize: 50,
      contentPanelMin: 30,
    }),
    [
      cotraderMode,
      workspaceConfig.chartPosition,
      workspaceConfig.gptPosition,
      workspaceConfig.shellLayout,
    ]
  );

  const resetLayout = () => {
    Object.keys(localStorage)
      .filter(
        (key) =>
          key.startsWith("react-resizable-panels:cotrader-layout-") ||
          key === workspaceStorageKey
      )
      .forEach((key) => localStorage.removeItem(key));
    setWorkspaceConfig(DEFAULT_FX_WORKSPACE);
    setActiveUpdatesTab("updates");
    setLayoutResetKey((prev) => prev + 1);
    setChartLayoutVersion((prev) => prev + 1);
  };

  const languageList = COTRADER_LANGUAGE_LIST;
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const menuButtonRef = useRef(null);
  const onSymbolClick = (symbol) => {
    const a = symbol.split("=");
    if (a.length > 1) {
      setSelectedSymbol(a[0]);
    } else {
      setActiveTvSymbol(symbol);
    }
  };
  const { insights, loading, sentiment, sentimentUpdatedAt } = useInsightsWs(
    selectedSymbol,
    updateSpeed,
    selectedSymbolRef,
    showCountDownInsights,
    language,
    1200,
    undefined,
    { isDemoMode },
  );
  const updatesScrollRef = useStablePrependScroll({
    itemCount: insights.length,
    enabled: currentUpdatesTab === "updates" && !loading,
    resetKey: `${selectedSymbol}:${language}`,
  });
  const { value: liveSentimentValue, delta: sentimentDelta } = useLiveSentiment(
    sentiment,
    selectedSymbol,
  );
  const fxSpotChartSymbol = useMemo(
    () => FX_SPOT_CHART_SYMBOL_MAP[selectedSymbol] || selectedSymbol,
    [selectedSymbol]
  );
  const fxSpotTickerStreamSymbol = useMemo(
    () => getFxSpotTickerStreamSymbol(selectedSymbol),
    [selectedSymbol]
  );
  const tvTickers = useMemo(
    () => mapFxSymbolsToTickerTape(fxAvailableSymbols, selectedSymbol),
    [fxAvailableSymbols, selectedSymbol]
  );
  const { quote: liveSpotQuote } = useLiveTickerQuote(fxSpotTickerStreamSymbol, {
    enabled: instrumentType === "FX_SPOT",
    isDemoMode,
  });
  const liveSpotDisplayPrice = useMemo(() => {
    const nextValue = Number(
      liveSpotQuote?.current_price ??
        liveSpotQuote?.spot_midpoint ??
        liveSpotQuote?.price ??
        liveSpotQuote?.last
    );

    return Number.isFinite(nextValue) ? nextValue : null;
  }, [liveSpotQuote]);

  useEffect(() => {
    if (
      typeof selectedSymbolOverride === "string" &&
      selectedSymbolOverride &&
      selectedSymbolOverride !== selectedSymbol
    ) {
      setSelectedSymbol(selectedSymbolOverride);
    }
  }, [selectedSymbol, selectedSymbolOverride]);

  useEffect(() => {
    if (
      typeof instrumentTypeOverride === "string" &&
      instrumentTypeOverride &&
      instrumentTypeOverride !== instrumentType
    ) {
      setInstrumentType(instrumentTypeOverride);
    }
  }, [instrumentType, instrumentTypeOverride]);

  useEffect(() => {
    localStorage.setItem(workspaceStorageKey, JSON.stringify(workspaceConfig));
  }, [workspaceConfig, workspaceStorageKey]);

  useEffect(() => {
    if (localStorage.getItem("user_language") !== language) {
      localStorage.setItem("user_language", language);
    }
  }, [language]);

  useEffect(() => {
    if (Array.isArray(availableSymbolsOverride)) {
      setFxAvailableSymbols(availableSymbolsOverride);
      return () => {};
    }

    let isCurrent = true;

    const loadFxSymbols = async () => {
      try {
        await fetchSymbols({
          setSymbols: (symbols) => {
            if (isCurrent) {
              setFxAvailableSymbols(Array.isArray(symbols) ? symbols : []);
            }
          },
          setIsLoading: () => {},
          instrument_type: "FX_SPOT",
        });
      } catch (error) {
        console.error("Failed to load FX symbols for ticker tape:", error);
      }
    };

    loadFxSymbols();

    return () => {
      isCurrent = false;
    };
  }, [availableSymbolsOverride]);

  const applyWorkspacePreset = (presetKey) => {
    setWorkspaceConfig(getFxWorkspacePresetConfig(presetKey));
    setLayoutResetKey((prev) => prev + 1);
    setChartLayoutVersion((prev) => prev + 1);
  };

  const updateWorkspaceConfig = (nextConfig) => {
    setWorkspaceConfig((previousConfig) =>
      normalizeFxWorkspace({
        ...previousConfig,
        ...nextConfig,
        preset: "custom",
      })
    );
    setLayoutResetKey((prev) => prev + 1);
    setChartLayoutVersion((prev) => prev + 1);
  };

  const fxComponent = {
    FX_SPOT: (
      <TradingViewChart
        symbol={fxSpotChartSymbol}
        streamSymbol={fxSpotTickerStreamSymbol}
        defaultInterval={chartIntervalOverride ?? "1m"}
        defaultPeriod={chartPeriodOverride ?? "5d"}
        intervalOverride={chartIntervalOverride}
        periodOverride={chartPeriodOverride}
        hideHeader={hideSelectionSummary}
        titleOverride={selectedSymbol}
        layoutVersion={chartLayoutVersion}
        liveQuote={liveSpotQuote}
      />
    ),
    FX_FORWARD: (
      <CoTraderBarChart
        selectedSymbol={selectedSymbol}
        tenor={tenor}
        setTenorList={setTenorList}
        layoutVersion={chartLayoutVersion}
      />
    ),
  };

  const handleAskFromUpdate = (question) => {
    if (typeof question !== "string" || !question.trim()) {
      return;
    }

    if (workspaceConfig.gptPosition === "collapsed") {
      setWorkspaceConfig((previousConfig) =>
        normalizeFxWorkspace({
          ...previousConfig,
          gptPosition: "right",
          preset: "custom",
        })
      );
      setLayoutResetKey((prev) => prev + 1);
    }

    setPendingFxQuestion({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text: question.trim(),
    });
  };

  const currentWorkspaceLabel =
    workspaceConfig.preset === "custom"
      ? "Custom"
      : FX_WORKSPACE_PRESETS[workspaceConfig.preset]?.label || "Desk";
  const hiddenModules = [
    showChart
      ? null
      : {
          label: "Show Chart",
          onClick: () => updateWorkspaceConfig({ chartPosition: "top" }),
        },
    showGpt
      ? null
      : {
          label: "Show FX GPT",
          onClick: () => updateWorkspaceConfig({ gptPosition: "right" }),
        },
  ].filter(Boolean);
  const workspaceActionButtons = hiddenModules.map((module) => (
    <button
      type="button"
      key={module.label}
      className="btn btn-sm rounded-pill"
      style={{
        background: "var(--app-panel-bg, #FFFFFF)",
        border: `1px solid ${WORKSPACE_THEME.border}`,
        color: WORKSPACE_THEME.text,
        fontWeight: 600,
      }}
      onClick={module.onClick}
    >
      {module.label}
    </button>
  ));
  const shouldPortalToolbar = Boolean(toolbarPortalTarget);
  const showHeaderSummary = !hideSelectionSummary;
  const showInlineHeaderToolbar = !shouldPortalToolbar;
  const showInlineHiddenModules = !shouldPortalToolbar && hiddenModules.length > 0;
  const showHeaderRow =
    showHeaderSummary || showInlineHiddenModules || showInlineHeaderToolbar;
  const workspaceToolbar = (
    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
      {shouldPortalToolbar ? workspaceActionButtons : null}
      <WorkspacePresetPicker
        presets={FX_WORKSPACE_PRESETS}
        activePreset={workspaceConfig.preset}
        label={currentWorkspaceLabel}
        onSelect={applyWorkspacePreset}
        onMoreOptions={() => setShowMenu(true)}
      />
      <div className="d-flex flex-row align-items-center">
        <div className="me-2">
          <div
            className="h7"
            style={{
              color: WORKSPACE_THEME.text,
              fontWeight: 700,
              fontSize: shouldPortalToolbar ? "0.86rem" : undefined,
              minWidth: shouldPortalToolbar ? "70px" : "84px",
              textAlign: "right",
            }}
          >
            {liveSpotDisplayPrice != null ? liveSpotDisplayPrice.toFixed(4) : "--"}
          </div>
        </div>
        <div>
          <div className="position-relative">
            <Button
              variant="outline-secondary"
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 d-flex align-items-center"
              ref={menuButtonRef}
              aria-haspopup="true"
              aria-expanded={showMenu}
            >
              <FaBars size={shouldPortalToolbar ? 18 : 20} />
            </Button>

            {showMenu && (
              <div
                ref={menuRef}
                className="bg-white shadow-lg rounded-3 p-3 position-absolute"
                style={{
                  right: 0,
                  top: "40px",
                  zIndex: 1050,
                  width: "min(340px, calc(100vw - 24px))",
                  maxHeight: "calc(100vh - 96px)",
                  border: `1px solid ${WORKSPACE_THEME.border}`,
                  display: "flex",
                  overflow: "visible",
                }}
                role="menu"
              >
                <div
                  className="d-flex flex-column gap-3"
                  style={{ flex: 1, minHeight: 0 }}
                >
                  {[
                    {
                      label: "Show Suggestions",
                      icon: "fa-lightbulb",
                      checked: showSuggestions,
                      onChange: () => setShowSuggestions(!showSuggestions),
                    },
                  ].map(({ label, icon, checked, onChange }) => (
                    <div
                      className="d-flex justify-content-between align-items-center"
                      key={label}
                    >
                      <span className="d-flex align-items-center text-secondary">
                        <i className={`fa ${icon} me-2`} />
                        {label}
                      </span>
                      <Switch
                        onChange={onChange}
                        checked={checked}
                        height={20}
                        width={40}
                      />
                    </div>
                  ))}

                  <div
                    className="d-flex flex-column gap-3 pe-1"
                    style={{
                      flex: "1 1 auto",
                      minHeight: 0,
                      overflowY: "auto",
                      overscrollBehavior: "contain",
                    }}
                  >
                    <WorkspaceSettingsSection
                      title="Workspace Presets"
                      collapseSignal={settingsCollapseSignal}
                    >
                      <div className="d-grid gap-2">
                        {Object.entries(FX_WORKSPACE_PRESETS).map(
                          ([presetKey, presetValue]) => {
                            const isActive =
                              workspaceConfig.preset === presetKey &&
                              workspaceConfig.preset !== "custom";

                            return (
                              <button
                                key={presetKey}
                                type="button"
                                className="btn btn-sm text-start rounded-3"
                                style={{
                                  background: isActive
                                    ? "rgba(13, 110, 253, 0.08)"
                                    : "var(--app-panel-bg, #FFFFFF)",
                                  border: isActive
                                    ? "1px solid rgba(13, 110, 253, 0.28)"
                                    : `1px solid ${WORKSPACE_THEME.border}`,
                                  color: WORKSPACE_THEME.text,
                                  padding: "0.65rem 0.8rem",
                                }}
                                onClick={() => applyWorkspacePreset(presetKey)}
                              >
                                <div
                                  style={{
                                    fontWeight: 700,
                                    fontSize: "0.86rem",
                                  }}
                                >
                                  {presetValue.label}
                                </div>
                                <div
                                  style={{
                                    color: WORKSPACE_THEME.muted,
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {presetValue.description}
                                </div>
                              </button>
                            );
                          }
                        )}
                      </div>
                    </WorkspaceSettingsSection>

                    <WorkspaceSettingsSection
                      title="Module Positions"
                      collapseSignal={settingsCollapseSignal}
                    >
                      {[
                        {
                          label: "Workspace Shape",
                          value: workspaceConfig.shellLayout,
                          options: [
                            ["stacked", "Stacked"],
                            ["assistantColumn", "Split"],
                          ],
                          onChange: (shellLayout) =>
                            updateWorkspaceConfig({ shellLayout }),
                        },
                        {
                          label: "Chart",
                          value: workspaceConfig.chartPosition,
                          options: [
                            ["top", "Top"],
                            ["bottom", "Bottom"],
                            ["collapsed", "Hidden"],
                          ],
                          onChange: (chartPosition) =>
                            updateWorkspaceConfig({ chartPosition }),
                        },
                        {
                          label: "FX GPT",
                          value: workspaceConfig.gptPosition,
                          options: [
                            ["right", "Right"],
                            ["left", "Left"],
                            ["collapsed", "Hidden"],
                          ],
                          onChange: (gptPosition) =>
                            updateWorkspaceConfig({ gptPosition }),
                        },
                      ].map((group) => (
                        <div
                          key={group.label}
                          className="d-flex align-items-center justify-content-between gap-2 mb-2"
                        >
                          <span
                            style={{
                              color: WORKSPACE_THEME.text,
                              fontSize: "0.84rem",
                              fontWeight: 600,
                              minWidth: "92px",
                            }}
                          >
                            {group.label}
                          </span>
                          <div className="d-flex gap-1 flex-wrap justify-content-end">
                            {group.options.map(([optionValue, optionLabel]) => {
                              const isActive = group.value === optionValue;

                              return (
                                <button
                                  key={optionValue}
                                  type="button"
                                  className="btn btn-sm rounded-pill"
                                  style={{
                                    background: isActive
                                      ? "#0D6EFD"
                                      : "var(--app-panel-bg, #FFFFFF)",
                                    color: isActive
                                      ? "#FFFFFF"
                                      : WORKSPACE_THEME.text,
                                    border: isActive
                                      ? "1px solid #0D6EFD"
                                      : `1px solid ${WORKSPACE_THEME.border}`,
                                    fontWeight: 600,
                                  }}
                                  onClick={() => group.onChange(optionValue)}
                                >
                                  {optionLabel}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </WorkspaceSettingsSection>
                  </div>

                  <Button variant="outline-danger" onClick={resetLayout}>
                    Reset Layout
                  </Button>

                  <div className="d-flex align-items-center w-100">
                    <div className="me-2 text-secondary">Language:</div>
                    <div className="flex-grow-1">
                      <Dropdown
                        onToggle={(nextShow) => {
                          if (nextShow) {
                            setSettingsCollapseSignal((current) => current + 1);
                          }
                        }}
                      >
                        <Dropdown.Toggle
                          variant="outline-secondary"
                          id="language-dropdown"
                          className="w-100"
                        >
                          {language}
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                          className="w-100"
                          style={{
                            maxHeight: "min(280px, calc(100vh - 120px))",
                            overflowY: "auto",
                          }}
                        >
                          {languageList.map((lang) => (
                            <Dropdown.Item
                              key={lang}
                              onClick={() => {
                                localStorage.setItem("user_language", lang);
                                setLanguage(lang);
                                setLanguageKey((prev) => prev + 1);
                              }}
                              active={language === lang}
                            >
                              {lang}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const horizontalResizeHandle = (
    <PanelResizeHandle
      style={{
        width: "6px",
        backgroundColor: "transparent",
        cursor: "col-resize",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "4px",
          height: "56px",
          borderRadius: "999px",
          background: "var(--app-grip, #d9e0e8)",
        }}
      />
    </PanelResizeHandle>
  );

  // Always available: drag to trade chart height for the insights/chat row.
  // Its 16px strip replaces the gap between the two, so spacing is unchanged.
  const verticalResizeHandle = (
    <PanelResizeHandle className="ct-resize-handle" title="Drag to resize the chart">
      <span className="ct-resize-grip" aria-hidden="true" />
    </PanelResizeHandle>
  );

  const renderFxChartSurface = (paddingClass) => (
    <div className={`${paddingClass} h-100`} style={{ minHeight: 0 }}>
      <div
        className={`ct-panel ct-panel--chart ${
          cotraderMode === "monitor" ? "is-focused" : ""
        }`}
      >
        {fxComponent[instrumentType]}
      </div>
    </div>
  );

  const renderFxGptSurface = (paddingClass) => (
    <div className={`${paddingClass} h-100`}>
      <div className={`ct-panel ${cotraderMode === "ask" ? "is-focused" : ""}`}>
        <div className={isDemoMode ? "ct-panel-scroll" : "h-100"}>
          <PersistentSlot node={gptHostNode} />
        </div>
      </div>
    </div>
  );

  const renderFxUpdatesSurface = (paddingClass) => (
    <div className={`${paddingClass} h-100`}>
      <CoTraderInsightsPanel
        tabs={FX_UPDATES_TABS}
        activeTab={currentUpdatesTab}
        onTabChange={setActiveUpdatesTab}
        sentimentValue={liveSentimentValue}
        sentimentDelta={sentimentDelta}
        sentimentUpdatedAt={sentimentUpdatedAt}
        loading={loading}
        updates={insights}
        scrollRef={updatesScrollRef}
        onAskQuestion={handleAskFromUpdate}
        renderTab={() => (
          <CoTraderExecutionDecision
            currencyPair={selectedSymbol}
            language={language}
          />
        )}
        isFocused={cotraderMode === "monitor"}
      />
    </div>
  );

  // Insights beside the assistant (either side), or alone when it is hidden.
  const renderMarketRow = (verticalPadding) => {
    if (!showGpt) {
      return renderFxUpdatesSurface(`px-3 ${verticalPadding}`);
    }

    const isGptLeft = workspaceConfig.gptPosition === "left";
    const gptPanel = (
      <Panel
        defaultSize={layoutState.fxgptPanelSize}
        minSize={layoutState.fxgptPanelMin}
      >
        {renderFxGptSurface(
          `${isGptLeft ? "ps-3 pe-2" : "ps-2 pe-3"} ${verticalPadding}`
        )}
      </Panel>
    );

    return (
      <PanelGroup
        key={`cotrader-horizontal-${workspaceConfig.gptPosition}-${layoutResetKey}`}
        direction="horizontal"
        autoSaveId={layoutState.horizontalId}
      >
        {isGptLeft ? (
          <>
            {gptPanel}
            {horizontalResizeHandle}
          </>
        ) : null}
        <Panel
          defaultSize={layoutState.marketPanelSize}
          minSize={layoutState.marketPanelMin}
          maxSize={layoutState.marketPanelMax}
        >
          {renderFxUpdatesSurface(
            `${isGptLeft ? "ps-2 pe-3" : "ps-3 pe-2"} ${verticalPadding}`
          )}
        </Panel>
        {!isGptLeft ? (
          <>
            {horizontalResizeHandle}
            {gptPanel}
          </>
        ) : null}
      </PanelGroup>
    );
  };

  const renderStackedWorkspace = () => {
    if (!showChart) {
      return renderMarketRow("pt-2 pb-3");
    }

    const chartPanel = (
      <Panel
        defaultSize={layoutState.chartPanelSize}
        minSize={layoutState.chartPanelMin}
        maxSize={layoutState.chartPanelMax}
      >
        {renderFxChartSurface(
          workspaceConfig.chartPosition === "bottom" ? "px-3 pt-0 pb-3" : "px-3 pt-2 pb-0"
        )}
      </Panel>
    );
    const marketPanel = (
      <Panel
        defaultSize={layoutState.bottomPanelSize}
        minSize={layoutState.bottomPanelMin}
      >
        {renderMarketRow(
          workspaceConfig.chartPosition === "bottom" ? "pt-2 pb-0" : "pt-0 pb-3"
        )}
      </Panel>
    );

    return (
      <PanelGroup
        direction="vertical"
        key={`cotrader-vertical-${workspaceConfig.chartPosition}-${layoutResetKey}`}
        autoSaveId={layoutState.verticalId}
        className="h-100"
        onLayout={() => {
          setChartLayoutVersion((prev) => prev + 1);
        }}
      >
        {workspaceConfig.chartPosition === "bottom" ? marketPanel : chartPanel}
        {verticalResizeHandle}
        {workspaceConfig.chartPosition === "bottom" ? chartPanel : marketPanel}
      </PanelGroup>
    );
  };

  // Chart and insights stacked in one column, with the assistant spanning
  // the full height beside them.
  const renderSplitContentColumn = (sidePadding) => {
    if (!showChart) {
      return renderFxUpdatesSurface(`${sidePadding} pt-2 pb-3`);
    }

    const isChartBottom = workspaceConfig.chartPosition === "bottom";
    const chartPanel = (
      <Panel
        defaultSize={layoutState.chartPanelSize}
        minSize={layoutState.chartPanelMin}
        maxSize={layoutState.chartPanelMax}
      >
        {renderFxChartSurface(`${sidePadding} ${isChartBottom ? "pt-0 pb-3" : "pt-2 pb-0"}`)}
      </Panel>
    );
    const updatesPanel = (
      <Panel
        defaultSize={layoutState.bottomPanelSize}
        minSize={layoutState.bottomPanelMin}
      >
        {renderFxUpdatesSurface(`${sidePadding} ${isChartBottom ? "pt-2 pb-0" : "pt-0 pb-3"}`)}
      </Panel>
    );

    return (
      <PanelGroup
        direction="vertical"
        autoSaveId={layoutState.contentVerticalId}
        key={`cotrader-content-${workspaceConfig.chartPosition}-${layoutResetKey}`}
        className="h-100"
        onLayout={() => {
          setChartLayoutVersion((prev) => prev + 1);
        }}
      >
        {isChartBottom ? updatesPanel : chartPanel}
        {verticalResizeHandle}
        {isChartBottom ? chartPanel : updatesPanel}
      </PanelGroup>
    );
  };

  const renderAssistantColumnWorkspace = () => {
    if (!showGpt) {
      return renderSplitContentColumn("px-3");
    }

    const isGptLeft = workspaceConfig.gptPosition === "left";
    const gptPanel = (
      <Panel
        defaultSize={layoutState.fxgptPanelSize}
        minSize={layoutState.fxgptPanelMin}
      >
        {renderFxGptSurface(`${isGptLeft ? "ps-3 pe-2" : "ps-2 pe-3"} pt-2 pb-3`)}
      </Panel>
    );

    return (
      <PanelGroup
        direction="horizontal"
        autoSaveId={layoutState.shellHorizontalId}
        key={`cotrader-shell-${workspaceConfig.gptPosition}-${layoutResetKey}`}
        className="h-100"
      >
        {isGptLeft ? (
          <>
            {gptPanel}
            {horizontalResizeHandle}
          </>
        ) : null}
        <Panel
          defaultSize={layoutState.contentPanelSize}
          minSize={layoutState.contentPanelMin}
        >
          {renderSplitContentColumn(isGptLeft ? "ps-2 pe-3" : "ps-3 pe-2")}
        </Panel>
        {!isGptLeft ? (
          <>
            {horizontalResizeHandle}
            {gptPanel}
          </>
        ) : null}
      </PanelGroup>
    );
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle} | EMAlpha</title>
      </Helmet>
      <div
        className="ct-page w-100 h-100 d-flex flex-column"
        style={{ minHeight: 0 }}
      >
        {!hideTopChrome && (
          <div className="px-3 pt-3 pb-2">
            <div className="ct-chrome px-3 py-2">
              <CoTraderCustomTickerTape
                symbols={tvTickers}
                onSymbolClick={onSymbolClick}
              />
            </div>
          </div>
        )}
        {!hideTopChrome && (
          <div className="px-3 pb-2">
            <div className="ct-chrome px-3 py-3">
              <CoTraderFilters
                setSelectedSymbol={setSelectedSymbol}
                setUpdateSpeed={setUpdateSpeed}
                tenorList={tenorList}
                setTenor={instrumentType === "FX_FORWARD" ? setTenor : null}
                setInstrumentType={setInstrumentType}
                instrumentType={instrumentType}
              />
            </div>
          </div>
        )}

        <div
          className="flex-grow-1 d-flex flex-column"
          key={languageKey}
          style={{ minHeight: 0 }}
        >
          {showHeaderRow ? (
            <div
              className={`px-3 pt-1 d-flex align-items-center ${
                showHeaderSummary || showInlineHiddenModules
                  ? "justify-content-between"
                  : "justify-content-end"
              }`}
            >
              {showHeaderSummary || showInlineHiddenModules ? (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {showHeaderSummary ? (
                    <>
                      <span className="ct-label">Pair</span>
                      <span
                        className="badge rounded-pill"
                        style={{
                          background: "var(--app-panel-bg, #FFFFFF)",
                          border: `1px solid ${WORKSPACE_THEME.border}`,
                          color: WORKSPACE_THEME.text,
                          fontWeight: 700,
                          fontSize: "0.82rem",
                          padding: "0.45rem 0.7rem",
                        }}
                      >
                        {selectedSymbol}
                      </span>
                    </>
                  ) : null}
                  {showInlineHiddenModules ? workspaceActionButtons : null}
                </div>
              ) : null}
              {showInlineHeaderToolbar ? workspaceToolbar : null}
            </div>
          ) : null}
          {shouldPortalToolbar
            ? createPortal(workspaceToolbar, toolbarPortalTarget)
            : null}
          {/* Mounted once regardless of layout; PersistentSlot places it. */}
          {createPortal(
            <GptPanelComponent
              selectedSymbol={selectedSymbol}
              searchFlag={searchFlag}
              setSearchFlag={setSearchFlag}
              showSuggestions={showSuggestions}
              setShowSuggestions={setShowSuggestions}
              tabs={tabs}
              setTabs={setTabs}
              useFxSuggestedQuestionsApi
              onConversationStateChange={setFxgptState}
              externalQuestionRequest={pendingFxQuestion}
            />,
            gptHostNode
          )}

          <div className="d-flex flex-grow-1 flex-column w-100" style={{ minHeight: 0 }}>
            {workspaceConfig.shellLayout === "assistantColumn"
              ? renderAssistantColumnWorkspace()
              : renderStackedWorkspace()}
          </div>
        </div>
      </div>
    </>
  );
};

export default CoTrader;
